import { useState, useEffect, useRef, FC } from "react";
import InputFrame from "../../../atoms/InputFrame";
import appConfig from "../../../../config/app-config";
import { Auth } from "aws-amplify";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

interface ProfessionAutocompleteProps {
    id?: string;
    label: string;
    value: string;
    displayValue?: string;
    className?: string;
    errorMessage?: string;
    onChange: (professionId: string, professionName: string) => void;
}

interface ProfessionOption {
    id: number;
    name: string;
}

const ProfessionAutocomplete: FC<ProfessionAutocompleteProps> = ({
    id = "profession", label, value, displayValue = "", className, errorMessage, onChange,
}) => {
    const [inputText, setInputText] = useState(displayValue);
    const [options, setOptions] = useState<ProfessionOption[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => { setInputText(displayValue); }, [displayValue]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getHeaders = async () => {
        const session = await Auth.currentSession();
        return {
            "x-api-key": PERSON_API_KEY,
            "Authorization": `Bearer ${session.getIdToken().getJwtToken()}`,
            "Content-Type": "application/json",
        };
    };

    const latestSearchRef = useRef("");

    const searchProfessions = async (search: string) => {
        if (search.length < 1) { setOptions([]); return; }
        latestSearchRef.current = search;
        setIsLoading(true);
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/professions?search=${encodeURIComponent(search)}`, { headers });
            if (res.ok && latestSearchRef.current === search) setOptions(await res.json());
        } catch { /* ignore */ }
        finally { if (latestSearchRef.current === search) setIsLoading(false); }
    };

    const handleInputChange = (text: string) => {
        setInputText(text);
        setIsOpen(true);
        if (value) onChange("", "");
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchProfessions(text), 300);
    };

    const handleSelect = (opt: ProfessionOption) => {
        setInputText(opt.name);
        onChange(String(opt.id), opt.name);
        setIsOpen(false);
    };

    const handleCreate = async () => {
        const name = inputText.trim();
        if (!name) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/professions`, {
                method: "POST", headers, body: JSON.stringify({ name }),
            });
            if (res.ok) {
                const created: ProfessionOption = await res.json();
                setInputText(created.name);
                onChange(String(created.id), created.name);
                setIsOpen(false);
            }
        } catch { /* ignore */ }
    };

    const error = Boolean(errorMessage);
    const borderColor = error ? "border-red-600" : "border-gray-300";
    const hasExactMatch = options.some(o => o.name.toLowerCase() === inputText.trim().toLowerCase());

    return (
        <InputFrame label={label} errorMessage={errorMessage} className={className}>
            <div ref={wrapperRef} className="relative w-full">
                <input
                    id={id}
                    type="text"
                    value={inputText}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => { if (inputText.length >= 1) searchProfessions(inputText); setIsOpen(true); }}
                    placeholder="Digite para buscar..."
                    autoComplete="off"
                    className={`block py-2.5 px-0 w-full text-sm bg-transparent border-0 border-b-2 ${borderColor} appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer dark:text-white`}
                />
                {isLoading && <span className="absolute right-0 top-2 text-xs text-gray-400">...</span>}
                {isOpen && (options.length > 0 || inputText.trim().length > 0) && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                        {options.map((opt) => (
                            <li key={opt.id} onClick={() => handleSelect(opt)}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50">
                                {opt.name}
                            </li>
                        ))}
                        {inputText.trim() && !hasExactMatch && (
                            <li onClick={handleCreate}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-green-50 text-green-700 border-t">
                                + Criar "{inputText.trim()}"
                            </li>
                        )}
                        {options.length === 0 && hasExactMatch && null}
                    </ul>
                )}
            </div>
        </InputFrame>
    );
};

export default ProfessionAutocomplete;
